import React, { useEffect, useState } from "react";
import { ShoppingCartIcon } from "@/components/parts/Icon";
import {
  AD_BOOK_SIZE,
  type AdBook,
  type AdCreative,
  type AdSize,
  type SlotBanner,
} from "@/domains/ads/AdConfig";
import { DESKTOP_MEDIA_QUERY, useMediaQuery } from "@/hooks/useMediaQuery";
import { SPONSORED_LINK_REL } from "@/lib/LinkRel";

export const BANNER_ROTATION_INTERVAL_MS = 8000;

function isSameSize(a: AdSize, b: AdSize): boolean {
  return a.width === b.width && a.height === b.height;
}

/**
 * 画面幅に合う素材を返す。画面幅が分からないサーバーでの描画では、どちらの幅でも同じサイズのときだけ返す
 */
function creativeForViewport(
  banner: SlotBanner,
  isDesktop: boolean | undefined,
): AdCreative | undefined {
  if (isDesktop === undefined) {
    return isSameSize(banner.mobile.size, banner.desktop.size)
      ? banner.desktop
      : undefined;
  }
  return isDesktop ? banner.desktop : banner.mobile;
}

/**
 * 素材を読み込む前から、画面幅ごとのサイズで場所を確保しておく
 */
function frameStyle(mobile: AdSize, desktop: AdSize): React.CSSProperties {
  return {
    "--ad-mobile-width": `${mobile.width}px`,
    "--ad-mobile-ratio": `${mobile.width}/${mobile.height}`,
    "--ad-desktop-width": `${desktop.width}px`,
    "--ad-desktop-ratio": `${desktop.width}/${desktop.height}`,
  } as React.CSSProperties;
}

type RotationItem =
  | { kind: "banner"; id: string; banner: SlotBanner }
  | { kind: "book"; id: string; book: AdBook };

const BookShopLink: React.FC<{ href: string; label: string }> = ({
  href,
  label,
}) => (
  <a
    href={href}
    rel={SPONSORED_LINK_REL}
    className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 underline decoration-primary-300 underline-offset-2 hover:text-primary-800"
  >
    <ShoppingCartIcon className="h-4 w-4 shrink-0" />
    {label}
  </a>
);

const BookCard: React.FC<{ book: AdBook }> = ({ book }) => (
  <div className="flex h-full w-full animate-fade-in items-center gap-3 bg-primary-50 p-3 motion-reduce:animate-none">
    {book.rakuten ? (
      <a
        href={book.rakuten.url}
        rel={SPONSORED_LINK_REL}
        aria-label={`${book.title}（楽天で見る）`}
        className="h-full max-w-[40%] shrink-0"
      >
        <img
          src={book.rakuten.imageUrl}
          alt=""
          loading="lazy"
          className="h-full w-full rounded border border-primary-100 bg-white object-contain shadow-sm"
        />
      </a>
    ) : (
      <div
        aria-hidden="true"
        className="h-full w-[35%] shrink-0 rounded border border-dashed border-primary-200 bg-white"
      />
    )}
    <div className="flex min-w-0 flex-col gap-2">
      <p className="line-clamp-4 text-sm font-semibold leading-snug text-primary-800">
        {book.title}
      </p>
      <p className="text-xs text-primary-500">{book.author}</p>
      <ul className="flex flex-col gap-1">
        <li>
          <BookShopLink href={book.amazonUrl} label="Amazonで見る" />
        </li>
        {book.rakuten && (
          <li>
            <BookShopLink href={book.rakuten.url} label="楽天で見る" />
          </li>
        )}
      </ul>
    </div>
  </div>
);

export interface BannerRotationProps {
  banners: readonly SlotBanner[];
  /** バナーのあとに続けて切り替える書籍。AD_BOOK_SIZE の枠に置くときだけ渡す */
  books?: readonly AdBook[];
}

/**
 * 広告のバナーを順に切り替えて表示する。PR表記と外側の見た目は、置く場所に合わせて呼び出し側で決める
 */
export const BannerRotation: React.FC<BannerRotationProps> = ({
  banners,
  books = [],
}) => {
  const items: RotationItem[] = [
    ...banners.map((banner): RotationItem => ({
      kind: "banner",
      id: banner.id,
      banner,
    })),
    ...books.map((book): RotationItem => ({ kind: "book", id: book.id, book })),
  ];
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);
  const prefersReducedMotion =
    useMediaQuery("(prefers-reduced-motion: reduce)") === true;
  const autoRotates = items.length > 1 && !paused && !prefersReducedMotion;

  useEffect(() => {
    if (!autoRotates) {
      return;
    }
    const timer = window.setInterval(
      () => setSelectedIndex((index) => (index + 1) % items.length),
      BANNER_ROTATION_INTERVAL_MS,
    );
    return () => window.clearInterval(timer);
  }, [autoRotates, items.length]);

  // クライアント側の遷移でページが変わってもこのコンポーネントは使い回されるので、選んでいた番号が広告の数を超えうる
  const activeIndex = Math.min(selectedIndex, items.length - 1);
  if (activeIndex < 0) {
    return null;
  }
  const activeItem = items[activeIndex];
  const creative =
    activeItem.kind === "banner"
      ? creativeForViewport(activeItem.banner, isDesktop)
      : undefined;

  return (
    <div
      className="relative w-fit max-w-full"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div
        style={
          activeItem.kind === "banner"
            ? frameStyle(
                activeItem.banner.mobile.size,
                activeItem.banner.desktop.size,
              )
            : frameStyle(AD_BOOK_SIZE, AD_BOOK_SIZE)
        }
        className="aspect-[var(--ad-mobile-ratio)] w-[var(--ad-mobile-width)] max-w-full overflow-hidden rounded-lg md:aspect-[var(--ad-desktop-ratio)] md:w-[var(--ad-desktop-width)]"
      >
        {activeItem.kind === "book" && (
          <BookCard key={activeItem.id} book={activeItem.book} />
        )}
        {activeItem.kind === "banner" && creative && (
          <a
            key={creative.linkUrl}
            href={creative.linkUrl}
            rel={SPONSORED_LINK_REL}
            className="block h-full w-full animate-fade-in motion-reduce:animate-none"
          >
            <img
              src={creative.imageUrl}
              width={creative.size.width}
              height={creative.size.height}
              alt={activeItem.banner.alt}
              loading="lazy"
              className="h-full w-full object-contain"
            />
          </a>
        )}
      </div>
      {creative && (
        <img
          key={creative.impressionUrl}
          src={creative.impressionUrl}
          width={1}
          height={1}
          alt=""
          className="pointer-events-none absolute left-0 top-0 h-px w-px opacity-0"
        />
      )}
      {items.length > 1 && (
        <div className="mt-3 flex justify-center gap-2">
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              aria-label={`広告${index + 1}を表示`}
              aria-current={index === activeIndex}
              onClick={() => setSelectedIndex(index)}
              className={`h-2 w-2 rounded-full transition-colors ${
                index === activeIndex
                  ? "bg-primary-500"
                  : "bg-primary-200 hover:bg-primary-300"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
